import { useEffect, useMemo, useState, useCallback, useRef, useId } from "react";
import axios from "axios";
import { Loader2, RefreshCw, WifiOff, ChevronDown, Star, Shirt, Trash2, X } from "lucide-react";

const API = "/api";
const AUTO_REFRESH_MS = 15000;

// Sørlandets Travpark nummerdekke-farger PER LØP (race number 1-12).
// Alle hester i samme løp har samme farge på nummerdekket.
// Format: [background, text color]
const RACE_BLANKET = {
  1:  ["#FFCC00", "#111111"], // gul
  2:  ["#1D4FAE", "#FFFFFF"], // blå
  3:  ["#E11D2B", "#FFFFFF"], // rød
  4:  ["#22C55E", "#FFFFFF"], // grønn
  5:  ["#FFFFFF", "#111111"], // hvit
  6:  ["#111111", "#FFFFFF"], // svart
  7:  ["#E7D3A1", "#111111"], // beige
  8:  ["#6A2A82", "#FFFFFF"], // lilla
  9:  ["#86D96C", "#111111"], // lys grønn
  10: ["#F9A8D4", "#111111"], // rosa
  11: ["#F97316", "#111111"], // oransje
  12: ["#78350F", "#FFFFFF"], // brun
};

const blanketFor = (raceNumber) => RACE_BLANKET[raceNumber] || ["#4B5563", "#FFFFFF"]; // nøytral grå for løp 13+

/**
 * Photographer-facing "Rangering" page — mobile-portrait first.
 * Photographer picks:
 *   1) Norwegian track (any track with racing that day)
 *   2) Game type available at that track (V65/V75/DD/…)
 *   3) View mode: single race or all races at once
 *   4) (single-race mode) Which race to see
 * Auto-refreshes odds every 15s. Keeps the phone screen awake via
 * the Screen Wake Lock API.
 */
export default function RangeringPage() {
  const [tracks, setTracks] = useState([]);          // [{raceday_key, track_name, date, game_types}]
  const [tracksLoading, setTracksLoading] = useState(true);
  const [tracksError, setTracksError] = useState(null);
  const [selectedTrackKey, setSelectedTrackKey] = useState(null);
  const [showTrackPicker, setShowTrackPicker] = useState(false);

  const [gameType, setGameType] = useState(null);
  const [game, setGame] = useState(null);
  const [selectedLeg, setSelectedLeg] = useState(1);
  const [viewMode, setViewMode] = useState("single"); // 'single' | 'all'

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  const [liveStatus, setLiveStatus] = useState(null); // omsetning, utbetalinger, vinnere
  const [silks, setSilks] = useState([]);             // kuskedrakter [{id, driver_name, body_color, sleeve_color}]
  const [showSilkEditor, setShowSilkEditor] = useState(false);

  const fetchSilks = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/driver-silks`, { timeout: 10000 });
      setSilks(res.data || []);
    } catch {}
  }, []);

  useEffect(() => { fetchSilks(); }, [fetchSilks]);

  const silksByName = useMemo(() => {
    const map = {};
    silks.forEach(s => { map[s.driver_name.trim().toLowerCase()] = s; });
    return map;
  }, [silks]);

  const selectedTrack = useMemo(
    () => tracks.find(t => t.raceday_key === selectedTrackKey) || null,
    [tracks, selectedTrackKey]
  );

  // Fetch Norwegian tracks list on mount
  useEffect(() => {
    let mounted = true;
    (async () => {
      setTracksLoading(true);
      setTracksError(null);
      try {
        const res = await axios.get(`${API}/rikstoto/norwegian-tracks`, { timeout: 15000 });
        if (!mounted) return;
        const list = (res.data?.tracks || []).filter(t => (t.game_types || []).length > 0);
        setTracks(list);
        // Default: Sørlandet if available, else first
        const preferred = list.find(t => t.track_name?.toLowerCase().includes("sørlandet")) || list[0];
        if (preferred) {
          setSelectedTrackKey(preferred.raceday_key);
          // Prefer V75 > V65 > V85/V86 > DD (V-games are the marquee bets)
          const priority = ["V75", "V65", "V85", "V86", "DD"];
          const best = priority.find(p => preferred.game_types.includes(p)) || preferred.game_types[0];
          setGameType(best);
        }
      } catch {
        if (mounted) setTracksError("Kunne ikke hente norske baner");
      } finally {
        if (mounted) setTracksLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Available "game types" at the selected track — always includes "ALL"
  // (every race that day, incl. standalone races outside V-games / DD).
  const availableGameTypes = useMemo(() => {
    if (!selectedTrack?.game_types) return ["ALL"];
    return ["ALL", ...selectedTrack.game_types];
  }, [selectedTrack]);

  // When track changes, pick a valid game type using V-priority
  useEffect(() => {
    if (!selectedTrack) return;
    if (!availableGameTypes.includes(gameType)) {
      const priority = ["V75", "V65", "V85", "V86", "DD", "ALL"];
      const best = priority.find(p => availableGameTypes.includes(p)) || availableGameTypes[0] || null;
      setGameType(best);
    }
  }, [selectedTrack, gameType, availableGameTypes]);

  // Fetch game data for track + gameType (special value "ALL" fetches every race)
  const fetchGame = useCallback(async (showSpinner) => {
    if (!selectedTrackKey || !gameType) return;
    if (showSpinner) setLoading(true);
    setError(null);
    try {
      const url = gameType === "ALL"
        ? `${API}/rikstoto/track/${selectedTrackKey}/all-races`
        : `${API}/rikstoto/track/${selectedTrackKey}/game/${gameType}`;
      const res = await axios.get(url, { timeout: 15000 });
      setGame(res.data);
      setLastFetched(new Date());
      // Live status: vinnere + omsetning + foreløpige utbetalinger (ikke-blokkerende)
      const pool = gameType === "ALL" ? "V65" : gameType;
      axios.get(`${API}/rikstoto/track/${selectedTrackKey}/live-status/${pool}`, { timeout: 15000 })
        .then(r => setLiveStatus(r.data))
        .catch(() => {});
    } catch (e) {
      const msg = e?.response?.status === 404
        ? gameType === "ALL"
          ? `Ingen løp for denne banen i dag`
          : `Ingen ${gameType}-løp funnet for denne banen i dag`
        : "Kunne ikke hente løpsdata. Prøv igjen.";
      setError(msg);
      setGame(null);
    } finally {
      setLoading(false);
    }
  }, [selectedTrackKey, gameType]);

  // Initial + on track/game change
  useEffect(() => {
    fetchGame(true);
    setSelectedLeg(1);
  }, [fetchGame]);

  // Auto-refresh silently every 15s
  useEffect(() => {
    if (!selectedTrackKey || !gameType) return;
    const t = setInterval(() => fetchGame(false), AUTO_REFRESH_MS);
    return () => clearInterval(t);
  }, [selectedTrackKey, gameType, fetchGame]);

  // Keep phone awake
  useEffect(() => {
    if (!("wakeLock" in navigator)) return;
    let sentinel = null;
    let cancelled = false;
    const request = async () => {
      try { sentinel = await navigator.wakeLock.request("screen"); } catch {}
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible" && !cancelled) request();
    };
    request();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      if (sentinel) { try { sentinel.release(); } catch {} }
    };
  }, []);

  const legs = useMemo(() => {
    if (!game?.races?.length) return [];
    return game.races.map(r => r.leg_number).filter(l => l != null).sort((a, b) => a - b);
  }, [game]);

  const selectedRace = useMemo(() => {
    if (!game?.races?.length) return null;
    return game.races.find(r => r.leg_number === selectedLeg) || game.races[0];
  }, [game, selectedLeg]);

  // --- Sveip for å bytte løp (single-modus) ---
  const [swipeDir, setSwipeDir] = useState(0); // -1 = forrige, 1 = neste (styrer slide-retning)
  const touchStartRef = useRef(null);

  const goLeg = useCallback((dir) => {
    if (!legs.length) return;
    const idx = legs.indexOf(selectedLeg);
    const next = idx + dir;
    if (next < 0 || next >= legs.length) return;
    setSwipeDir(dir);
    setSelectedLeg(legs[next]);
  }, [legs, selectedLeg]);

  const onTouchStart = useCallback((e) => {
    const t = e.touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY };
  }, []);

  const onTouchEnd = useCallback((e) => {
    const s = touchStartRef.current;
    touchStartRef.current = null;
    if (!s || viewMode !== "single") return;
    const t = e.changedTouches[0];
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      goLeg(dx < 0 ? 1 : -1);
    }
  }, [viewMode, goLeg]);

  // Scroll aktiv løps-chip inn i synsfeltet når løp byttes (f.eks. via sveip)
  useEffect(() => {
    const chip = document.querySelector(`[data-testid="leg-chip-${selectedLeg}"]`);
    if (chip) chip.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [selectedLeg]);

  // Return the top-4 horses of a race sorted by distribution desc (fallback odds asc).
  // Vinneren tas alltid med (legges til som ekstra rad hvis utenfor topp 4).
  const rankHorses = useCallback((horses, winnerNumbers = []) => {
    if (!horses?.length) return [];
    const anyDist = horses.some(h => (h.distribution || 0) > 0);
    const sorted = [...horses].sort((a, b) => {
      if (anyDist) return (b.distribution || 0) - (a.distribution || 0);
      return (a.odds || 999) - (b.odds || 999);
    });
    const top = sorted.slice(0, 4);
    winnerNumbers.forEach(wn => {
      if (!top.some(h => h.number === wn)) {
        const winner = sorted.find(h => h.number === wn);
        if (winner) top.push(winner);
      }
    });
    return top;
  }, []);

  const formatStart = (t) => {
    if (!t) return "";
    try { return new Date(t).toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit" }); }
    catch { return ""; }
  };

  return (
    <div
      data-testid="rangering-page"
      style={{
        minHeight: "100vh", backgroundColor: "#050F23", color: "#FFFFFF",
        fontFamily: "'Inter', sans-serif",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <style>{`
        @keyframes rangering-fade-in { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
        .rank-row { animation: rangering-fade-in 0.25s ease-out both; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        @keyframes rangering-slide-next { from { transform: translateX(60px); opacity: 0; } to { transform: none; opacity: 1; } }
        @keyframes rangering-slide-prev { from { transform: translateX(-60px); opacity: 0; } to { transform: none; opacity: 1; } }
        .race-slide-next { animation: rangering-slide-next 0.22s ease-out both; }
        .race-slide-prev { animation: rangering-slide-prev 0.22s ease-out both; }
      `}</style>

      {/* Sticky header */}
      <div style={{
        position: "sticky", top: 0, zIndex: 10,
        backgroundColor: "#050F23",
        borderBottom: "1px solid rgba(255,215,0,0.25)",
        paddingBottom: "8px",
      }}>
        <div style={{ padding: "14px 16px 8px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
            <div style={{
              fontFamily: "'Barlow Condensed', sans-serif", fontSize: "1.35rem", fontWeight: 700,
              letterSpacing: "0.06em", textTransform: "uppercase", color: "#FFD700",
            }}>
              Rangering
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <button
                onClick={() => setShowSilkEditor(true)}
                data-testid="silk-editor-btn"
                style={{
                  background: "transparent", border: "1px solid rgba(255,255,255,0.15)",
                  color: "rgba(255,255,255,0.7)", borderRadius: "8px",
                  padding: "6px 10px", display: "flex", alignItems: "center", gap: "6px",
                  fontSize: "0.75rem", cursor: "pointer",
                }}
              >
                <Shirt size={13} />
                Drakter
              </button>
              <button
                onClick={() => fetchGame(true)}
                data-testid="refresh-btn"
                disabled={loading}
                style={{
                  background: "transparent", border: "1px solid rgba(255,255,255,0.15)",
                  color: "rgba(255,255,255,0.7)", borderRadius: "8px",
                  padding: "6px 10px", display: "flex", alignItems: "center", gap: "6px",
                  fontSize: "0.75rem", cursor: "pointer",
                }}
              >
                <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
                Oppdater
              </button>
            </div>
          </div>

          {/* Track picker (dropdown) */}
          <button
            data-testid="track-picker-btn"
            onClick={() => setShowTrackPicker(v => !v)}
            disabled={tracksLoading || tracks.length === 0}
            style={{
              width: "100%", padding: "16px 18px",
              backgroundColor: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,215,0,0.35)", borderRadius: "12px",
              color: "#FFFFFF", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              gap: "12px",
            }}
          >
            <div style={{ textAlign: "left", overflow: "hidden" }}>
              <div style={{
                fontSize: "0.65rem", textTransform: "uppercase",
                letterSpacing: "0.14em", color: "rgba(255,255,255,0.45)", marginBottom: "2px",
              }}>
                Bane
              </div>
              <div style={{
                fontFamily: "'Barlow Condensed', sans-serif", fontSize: "1.35rem",
                fontWeight: 700, letterSpacing: "0.04em", color: "#FFD700",
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              }}>
                {tracksLoading ? "Henter baner…" :
                 tracksError ? "Feil ved henting" :
                 selectedTrack ? selectedTrack.track_name : "Velg bane"}
              </div>
            </div>
            <ChevronDown size={20} style={{ color: "rgba(255,255,255,0.5)", transform: showTrackPicker ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
          </button>

          {showTrackPicker && !tracksLoading && tracks.length > 0 && (
            <div
              data-testid="track-list"
              style={{
                marginTop: "8px", backgroundColor: "#0B1830",
                border: "1px solid rgba(255,215,0,0.25)", borderRadius: "12px",
                overflow: "hidden",
              }}
            >
              {tracks.map((t, i) => {
                const active = t.raceday_key === selectedTrackKey;
                return (
                  <button
                    key={t.raceday_key}
                    data-testid={`track-option-${t.raceday_key}`}
                    onClick={() => { setSelectedTrackKey(t.raceday_key); setShowTrackPicker(false); }}
                    style={{
                      width: "100%", padding: "14px 18px",
                      border: "none", cursor: "pointer",
                      backgroundColor: active ? "rgba(255,215,0,0.12)" : "transparent",
                      color: "#FFFFFF", textAlign: "left",
                      borderTop: i > 0 ? "1px solid rgba(255,255,255,0.05)" : "none",
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <span style={{
                      fontFamily: "'Barlow Condensed', sans-serif", fontSize: "1.2rem",
                      fontWeight: 700, letterSpacing: "0.04em",
                      color: active ? "#FFD700" : "#FFFFFF",
                    }}>
                      {t.track_name}
                    </span>
                    <span style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.5)" }}>
                      {t.game_types.join(" · ")}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Game type tabs (from selected track — always includes "Alle") */}
        {availableGameTypes.length > 0 && (
          <div style={{ display: "flex", gap: "10px", padding: "0 16px", flexWrap: "wrap" }} data-testid="game-type-tabs">
            {availableGameTypes.map(gt => {
              const active = gt === gameType;
              const label = gt === "ALL" ? "Alle" : gt;
              return (
                <button
                  key={gt}
                  data-testid={`game-type-${gt}`}
                  onClick={() => setGameType(gt)}
                  style={{
                    flex: 1, minWidth: "80px", padding: "18px 0", minHeight: "58px",
                    fontFamily: "'Barlow Condensed', sans-serif", fontSize: "1.5rem",
                    fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
                    border: active ? "2px solid #FFD700" : "1px solid rgba(255,255,255,0.15)",
                    backgroundColor: active ? "rgba(255,215,0,0.12)" : "transparent",
                    color: active ? "#FFD700" : "rgba(255,255,255,0.75)",
                    borderRadius: "12px", cursor: "pointer",
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}

        {/* View mode toggle */}
        <div style={{ display: "flex", gap: "10px", padding: "12px 16px 0" }} data-testid="view-mode-tabs">
          {[
            { key: "single", label: "Ett løp" },
            { key: "all",    label: "Alle løp" },
          ].map(m => {
            const active = m.key === viewMode;
            return (
              <button
                key={m.key}
                data-testid={`view-mode-${m.key}`}
                onClick={() => setViewMode(m.key)}
                style={{
                  flex: 1, padding: "12px 0", minHeight: "46px",
                  fontFamily: "'Inter', sans-serif", fontSize: "0.95rem",
                  fontWeight: 600, letterSpacing: "0.04em",
                  border: active ? "2px solid #FFD700" : "1px solid rgba(255,255,255,0.15)",
                  backgroundColor: active ? "rgba(255,215,0,0.12)" : "transparent",
                  color: active ? "#FFD700" : "rgba(255,255,255,0.75)",
                  borderRadius: "10px", cursor: "pointer",
                }}
              >
                {m.label}
              </button>
            );
          })}
        </div>

        {/* Race chips only in single-race mode */}
        {viewMode === "single" && legs.length > 0 && (
          <div
            className="no-scrollbar"
            data-testid="leg-chips"
            style={{
              display: "flex", gap: "10px", overflowX: "auto",
              padding: "14px 16px 6px", scrollSnapType: "x mandatory",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {legs.map(l => {
              const active = l === selectedLeg;
              return (
                <button
                  key={l}
                  data-testid={`leg-chip-${l}`}
                  onClick={() => { setSwipeDir(l > selectedLeg ? 1 : -1); setSelectedLeg(l); }}
                  style={{
                    flexShrink: 0, scrollSnapAlign: "start",
                    padding: "14px 22px", minWidth: "92px", minHeight: "52px",
                    fontFamily: "'Barlow Condensed', sans-serif", fontSize: "1.35rem",
                    fontWeight: 700, letterSpacing: "0.05em",
                    borderRadius: "999px", cursor: "pointer",
                    border: active ? "2px solid #FFD700" : "1px solid rgba(255,255,255,0.15)",
                    backgroundColor: active ? "#FFD700" : "transparent",
                    color: active ? "#050F23" : "rgba(255,255,255,0.8)",
                  }}
                >
                  {gameType === "ALL" ? `Løp ${l}` : `${gameType}-${l}`}
                </button>
              );
            })}
          </div>
        )}

        <div style={{ padding: "6px 16px 0", fontSize: "0.72rem", color: "rgba(255,255,255,0.4)" }}>
          {lastFetched && (
            <span>Oppdatert <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              {lastFetched.toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </span></span>
          )}
        </div>
      </div>

      {/* Body — sveip venstre/høyre for å bytte løp i single-modus */}
      <div
        data-testid="rangering-body"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        style={{ padding: "10px 16px 40px" }}
      >
        {loading && !game && (
          <div data-testid="loading-state" style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "48px 0", color: "rgba(255,255,255,0.5)",
          }}>
            <Loader2 size={24} className="animate-spin" />
            <span style={{ marginLeft: "10px" }}>Henter {gameType}-data…</span>
          </div>
        )}

        {error && (
          <div data-testid="error-state" style={{
            marginTop: "20px", padding: "16px",
            border: "1px solid rgba(220,38,38,0.4)",
            backgroundColor: "rgba(220,38,38,0.08)",
            borderRadius: "10px", color: "#FCA5A5",
            display: "flex", alignItems: "center", gap: "10px",
          }}>
            <WifiOff size={18} />
            <span style={{ fontSize: "0.9rem" }}>{error}</span>
          </div>
        )}

        {/* Omsetning + foreløpige utbetalinger */}
        {!loading && !error && (liveStatus?.turnover > 0 || liveStatus?.payouts?.length > 0) && (
          <PoolStatusCard
            status={liveStatus}
            poolLabel={gameType === "ALL" ? "V65" : gameType}
          />
        )}

        {/* SINGLE RACE VIEW */}
        {!loading && !error && viewMode === "single" && selectedRace && (
          <div
            key={selectedLeg}
            className={swipeDir === 1 ? "race-slide-next" : swipeDir === -1 ? "race-slide-prev" : ""}
            data-testid="single-race-view"
          >
            <RaceHeader race={selectedRace} startStr={formatStart(selectedRace.start_time)} />
            <HorseList
              horses={rankHorses(selectedRace.horses, liveStatus?.winners?.[selectedRace.race_number] || [])}
              raceKey={`${selectedLeg}`}
              raceNumber={selectedRace.race_number}
              winners={liveStatus?.winners?.[selectedRace.race_number] || []}
              silksByName={silksByName}
            />
          </div>
        )}

        {/* ALL RACES VIEW */}
        {!loading && !error && viewMode === "all" && game?.races?.length > 0 && game.races.map(race => (
          <div key={race.leg_number} style={{ marginBottom: "24px" }}>
            <div
              data-testid={`all-race-header-${race.leg_number}`}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "10px 4px", marginBottom: "8px",
                borderBottom: "1px solid rgba(255,215,0,0.25)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
                <div style={{
                  fontFamily: "'Barlow Condensed', sans-serif", fontSize: "1.55rem",
                  fontWeight: 700, letterSpacing: "0.06em", color: "#FFD700",
                }}>
                  {gameType === "ALL" ? `Løp ${race.race_number}` : `${gameType}-${race.leg_number}`}
                </div>
                <div style={{ fontSize: "0.9rem", color: "rgba(255,255,255,0.6)", whiteSpace: "nowrap" }}>
                  {gameType === "ALL"
                    ? (race.distance ? `${race.distance}m` : "")
                    : `Løp ${race.race_number}${race.distance ? ` · ${race.distance}m` : ""}`}
                </div>
                <StartMethodBadge method={race.start_method} />
              </div>
              {formatStart(race.start_time) && (
                <div style={{
                  fontFamily: "'JetBrains Mono', monospace", fontSize: "1.05rem",
                  fontWeight: 700, color: "#FFD700",
                }}>
                  {formatStart(race.start_time)}
                </div>
              )}
            </div>
            <HorseList
              horses={rankHorses(race.horses, liveStatus?.winners?.[race.race_number] || [])}
              raceKey={`all-${race.leg_number}`}
              raceNumber={race.race_number}
              winners={liveStatus?.winners?.[race.race_number] || []}
              silksByName={silksByName}
            />
          </div>
        ))}
      </div>

      {/* Drakt-editor */}
      {showSilkEditor && (
        <SilkEditor
          game={game}
          silks={silks}
          onClose={() => setShowSilkEditor(false)}
          onSaved={fetchSilks}
        />
      )}
    </div>
  );
}

function SilkIcon({ body, sleeve, helmet, pattern = "solid", patternColor = "#FFFFFF", size = 22 }) {
  const uid = useId().replace(/:/g, "");
  const bodyPath = "M7.2 11.6 L9.6 10.2 C10.2 11.6 13.8 11.6 14.4 10.2 L16.8 11.6 L16 17.4 L16 27.5 L8 27.5 L8 17.4 Z";
  return (
    <svg
      viewBox="0 0 24 29"
      width={size}
      height={size * 29 / 24}
      style={{ flexShrink: 0, display: "block" }}
      aria-hidden="true"
    >
      {/* hjelm */}
      <path d="M8 8.4 A4 4.2 0 0 1 16 8.4 Z" fill={helmet || body} stroke="rgba(255,255,255,0.35)" strokeWidth="0.6" />
      <rect x="7.5" y="8.1" width="9" height="1" rx="0.5" fill="rgba(0,0,0,0.4)" />
      {/* venstre erme */}
      <path d="M7.2 11.6 L2.2 15.2 L4.4 19.6 L8 17.4 Z" fill={sleeve} stroke="rgba(255,255,255,0.35)" strokeWidth="0.6" />
      {/* høyre erme */}
      <path d="M16.8 11.6 L21.8 15.2 L19.6 19.6 L16 17.4 Z" fill={sleeve} stroke="rgba(255,255,255,0.35)" strokeWidth="0.6" />
      {/* kropp */}
      <path d={bodyPath} fill={body} stroke="rgba(255,255,255,0.35)" strokeWidth="0.6" />
      {/* mønster klippet til kroppen */}
      <clipPath id={`silkclip-${uid}`}><path d={bodyPath} /></clipPath>
      <g clipPath={`url(#silkclip-${uid})`}>
        {pattern === "striped" && (
          <>
            <rect x="9.2" y="9" width="1.8" height="20" fill={patternColor} />
            <rect x="13" y="9" width="1.8" height="20" fill={patternColor} />
          </>
        )}
        {pattern === "diagonal" && (
          <polygon points="6.5,13.5 17.5,20.5 17.5,24 6.5,17" fill={patternColor} />
        )}
        {pattern === "split" && (
          <rect x="12" y="9" width="6" height="20" fill={patternColor} />
        )}
      </g>
      {/* krage */}
      <path d="M9.6 10.2 C10.2 11.6 13.8 11.6 14.4 10.2 L13.6 12 C12.8 12.8 11.2 12.8 10.4 12 Z" fill="rgba(0,0,0,0.35)" />
    </svg>
  );
}

const SILK_PATTERNS = [
  { key: "solid", label: "Ensfarget" },
  { key: "striped", label: "Striper" },
  { key: "diagonal", label: "Skråbånd" },
  { key: "split", label: "Todelt" },
];

function ColorField({ label, value, onChange, testId }) {
  return (
    <label style={{
      display: "flex", flexDirection: "column", alignItems: "center", gap: "2px",
      fontSize: "0.55rem", color: "rgba(255,255,255,0.45)",
      textTransform: "uppercase", letterSpacing: "0.08em",
    }}>
      <input
        type="color"
        value={value}
        onChange={e => onChange(e.target.value)}
        data-testid={testId}
        style={{ width: "38px", height: "32px", border: "none", background: "transparent", padding: 0, cursor: "pointer" }}
      />
      {label}
    </label>
  );
}

function SilkEditorRow({ name, silk, onSave, onDelete }) {
  const [body, setBody] = useState(silk?.body_color || "#1D4FAE");
  const [sleeve, setSleeve] = useState(silk?.sleeve_color || "#E11D2B");
  const [pattern, setPattern] = useState(silk?.pattern || "solid");
  const [patternColor, setPatternColor] = useState(silk?.pattern_color || "#FFFFFF");
  const [helmet, setHelmet] = useState(silk?.helmet_color || silk?.body_color || "#1D4FAE");
  const [saving, setSaving] = useState(false);
  const dirty = !silk
    || body !== silk.body_color
    || sleeve !== silk.sleeve_color
    || pattern !== (silk.pattern || "solid")
    || patternColor !== (silk.pattern_color || "#FFFFFF")
    || helmet !== (silk.helmet_color || silk.body_color);
  const save = async () => {
    setSaving(true);
    try { await onSave(name, { body, sleeve, pattern, patternColor, helmet }); } finally { setSaving(false); }
  };
  return (
    <div
      data-testid={`silk-row-${name}`}
      style={{
        padding: "12px", borderRadius: "10px",
        backgroundColor: "rgba(255,255,255,0.04)", marginBottom: "10px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <SilkIcon body={body} sleeve={sleeve} helmet={helmet} pattern={pattern} patternColor={patternColor} size={38} />
        <div style={{ flex: 1, fontSize: "0.98rem", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {name}
        </div>
        {dirty ? (
          <button
            onClick={save}
            disabled={saving}
            data-testid={`silk-save-${name}`}
            style={{
              padding: "9px 14px", borderRadius: "8px", border: "none", cursor: "pointer",
              backgroundColor: "#FFD700", color: "#050F23", fontWeight: 700, fontSize: "0.8rem",
            }}
          >
            {saving ? "…" : "Lagre"}
          </button>
        ) : (
          <button
            onClick={() => onDelete(silk)}
            data-testid={`silk-delete-${name}`}
            style={{
              padding: "9px", borderRadius: "8px", border: "1px solid rgba(220,38,38,0.4)",
              background: "transparent", color: "#FCA5A5", cursor: "pointer", display: "flex",
            }}
          >
            <Trash2 size={15} />
          </button>
        )}
      </div>
      <div style={{ display: "flex", gap: "6px", marginTop: "10px", flexWrap: "wrap" }}>
        {SILK_PATTERNS.map(p => (
          <button
            key={p.key}
            onClick={() => setPattern(p.key)}
            data-testid={`silk-pattern-${p.key}-${name}`}
            style={{
              padding: "7px 11px", borderRadius: "999px", cursor: "pointer",
              fontSize: "0.72rem", fontWeight: 600, letterSpacing: "0.03em",
              border: pattern === p.key ? "1.5px solid #FFD700" : "1px solid rgba(255,255,255,0.18)",
              backgroundColor: pattern === p.key ? "rgba(255,215,0,0.15)" : "transparent",
              color: pattern === p.key ? "#FFD700" : "rgba(255,255,255,0.65)",
            }}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", gap: "14px", marginTop: "10px", flexWrap: "wrap" }}>
        <ColorField label="Drakt" value={body} onChange={setBody} testId={`silk-body-${name}`} />
        {pattern !== "solid" && (
          <ColorField label="Mønster" value={patternColor} onChange={setPatternColor} testId={`silk-pattern-color-${name}`} />
        )}
        <ColorField label="Ermer" value={sleeve} onChange={setSleeve} testId={`silk-sleeve-${name}`} />
        <ColorField label="Hjelm" value={helmet} onChange={setHelmet} testId={`silk-helmet-${name}`} />
      </div>
    </div>
  );
}

function SilkEditor({ game, silks, onClose, onSaved }) {
  const [newName, setNewName] = useState("");
  const silkMap = useMemo(() => {
    const m = {};
    silks.forEach(s => { m[s.driver_name.trim().toLowerCase()] = s; });
    return m;
  }, [silks]);

  // Dagens kusker fra lastet løpsdata
  const todaysDrivers = useMemo(() => {
    const set = new Set();
    (game?.races || []).forEach(r => (r.horses || []).forEach(h => {
      if (h.driver) set.add(h.driver.trim());
    }));
    return [...set].sort((a, b) => a.localeCompare(b, "nb"));
  }, [game]);

  const otherSilks = useMemo(() => {
    const todayKeys = new Set(todaysDrivers.map(d => d.toLowerCase()));
    return silks.filter(s => !todayKeys.has(s.driver_name.trim().toLowerCase()));
  }, [silks, todaysDrivers]);

  const save = async (name, cfg) => {
    await axios.post(`${API}/driver-silks`, {
      driver_name: name,
      body_color: cfg.body,
      sleeve_color: cfg.sleeve,
      pattern: cfg.pattern,
      pattern_color: cfg.patternColor,
      helmet_color: cfg.helmet,
    });
    onSaved();
  };
  const remove = async (silk) => {
    if (!silk?.id) return;
    await axios.delete(`${API}/driver-silks/${silk.id}`);
    onSaved();
  };
  const addNew = async () => {
    const n = newName.trim();
    if (!n) return;
    await save(n, { body: "#1D4FAE", sleeve: "#E11D2B", pattern: "solid", patternColor: "#FFFFFF", helmet: "#1D4FAE" });
    setNewName("");
  };

  return (
    <div
      data-testid="silk-editor"
      style={{
        position: "fixed", inset: 0, zIndex: 60,
        backgroundColor: "#050F23", overflowY: "auto",
        WebkitOverflowScrolling: "touch",
      }}
    >
      <div style={{
        position: "sticky", top: 0, zIndex: 5, backgroundColor: "#050F23",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "14px 16px", borderBottom: "1px solid rgba(255,215,0,0.25)",
      }}>
        <div style={{
          fontFamily: "'Barlow Condensed', sans-serif", fontSize: "1.35rem", fontWeight: 700,
          letterSpacing: "0.06em", textTransform: "uppercase", color: "#FFD700",
          display: "flex", alignItems: "center", gap: "10px",
        }}>
          <Shirt size={20} />
          Kuskedrakter
        </div>
        <button
          onClick={onClose}
          data-testid="silk-editor-close"
          style={{
            background: "transparent", border: "1px solid rgba(255,255,255,0.2)",
            color: "#fff", borderRadius: "8px", padding: "8px", cursor: "pointer", display: "flex",
          }}
        >
          <X size={18} />
        </button>
      </div>

      <div style={{ padding: "14px 16px 40px" }}>
        <div style={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.5)", marginBottom: "14px", lineHeight: 1.4 }}>
          Velg drakt- og ermefarge per kusk. Fargene lagres og brukes automatisk i rangeringslista.
          Finn registrerte farger i Sportsbasen på travsport.no.
        </div>

        {todaysDrivers.length > 0 && (
          <>
            <div style={{
              fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.14em",
              color: "#FFD700", margin: "6px 0 10px",
            }}>
              Dagens kusker
            </div>
            {todaysDrivers.map(name => (
              <SilkEditorRow
                key={name}
                name={name}
                silk={silkMap[name.toLowerCase()]}
                onSave={save}
                onDelete={remove}
              />
            ))}
          </>
        )}

        {otherSilks.length > 0 && (
          <>
            <div style={{
              fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.14em",
              color: "rgba(255,255,255,0.5)", margin: "18px 0 10px",
            }}>
              Andre lagrede kusker
            </div>
            {otherSilks.map(s => (
              <SilkEditorRow
                key={s.id}
                name={s.driver_name}
                silk={s}
                onSave={save}
                onDelete={remove}
              />
            ))}
          </>
        )}

        <div style={{
          display: "flex", gap: "10px", marginTop: "20px",
          paddingTop: "16px", borderTop: "1px solid rgba(255,255,255,0.1)",
        }}>
          <input
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="Legg til kusk (navn)…"
            data-testid="silk-new-name"
            style={{
              flex: 1, padding: "12px 14px", borderRadius: "10px",
              border: "1px solid rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.05)",
              color: "#fff", fontSize: "0.95rem", outline: "none",
            }}
          />
          <button
            onClick={addNew}
            data-testid="silk-add-btn"
            style={{
              padding: "12px 18px", borderRadius: "10px", border: "none", cursor: "pointer",
              backgroundColor: "#FFD700", color: "#050F23", fontWeight: 700,
            }}
          >
            Legg til
          </button>
        </div>
      </div>
    </div>
  );
}

function PoolStatusCard({ status, poolLabel }) {
  const fmt = (n) => new Intl.NumberFormat("nb-NO", { maximumFractionDigits: 0 }).format(n);
  return (
    <div
      data-testid="pool-status-card"
      style={{
        marginBottom: "14px", padding: "14px 16px",
        backgroundColor: "rgba(255,215,0,0.06)",
        border: "1px solid rgba(255,215,0,0.3)", borderRadius: "14px",
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "12px" }}>
        <div style={{
          fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.14em",
          color: "rgba(255,255,255,0.5)",
        }}>
          {poolLabel}-omsetning
        </div>
        <div
          data-testid="pool-turnover"
          style={{
            fontFamily: "'JetBrains Mono', monospace", fontSize: "1.3rem",
            fontWeight: 700, color: "#FFD700",
          }}
        >
          {fmt(status.turnover)} kr
        </div>
      </div>
      {status.payouts?.length > 0 && (
        <div style={{ marginTop: "10px", paddingTop: "10px", borderTop: "1px solid rgba(255,215,0,0.18)" }}>
          <div style={{
            fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.14em",
            color: "rgba(255,255,255,0.5)", marginBottom: "6px",
          }}>
            Foreløpig utbetaling
          </div>
          <div style={{ display: "flex", gap: "18px", flexWrap: "wrap" }}>
            {status.payouts.map(p => (
              <div key={p.correct} data-testid={`payout-${p.correct}`} style={{ display: "flex", alignItems: "baseline", gap: "7px" }}>
                <span style={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.7)" }}>{p.correct} rette</span>
                <span style={{
                  fontFamily: "'JetBrains Mono', monospace", fontSize: "1.05rem",
                  fontWeight: 700, color: "#FFFFFF",
                }}>
                  {fmt(p.amount)} kr
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StartMethodBadge({ method }) {
  if (!method) return null;
  const m = String(method).toLowerCase();
  if (m.includes("unknown")) return null;
  const cfg = m.includes("auto")
    ? { label: "AUTOSTART", color: "#7EB8FF", bg: "rgba(59,130,246,0.16)", border: "rgba(96,165,250,0.5)" }
    : m.includes("volt")
      ? { label: "VOLTESTART", color: "#FFC98A", bg: "rgba(249,115,22,0.14)", border: "rgba(251,146,60,0.5)" }
      : { label: String(method).toUpperCase(), color: "rgba(255,255,255,0.75)", bg: "rgba(255,255,255,0.08)", border: "rgba(255,255,255,0.25)" };
  return (
    <span
      data-testid="start-method-badge"
      style={{
        display: "inline-block", padding: "3px 10px", borderRadius: "999px",
        fontFamily: "'Barlow Condensed', sans-serif", fontSize: "0.85rem",
        fontWeight: 700, letterSpacing: "0.1em",
        color: cfg.color, backgroundColor: cfg.bg, border: `1px solid ${cfg.border}`,
        verticalAlign: "middle", whiteSpace: "nowrap",
      }}
    >
      {cfg.label}
    </span>
  );
}

function RaceHeader({ race, startStr }) {
  return (
    <div
      data-testid="race-header"
      style={{
        padding: "8px 4px 12px",
        display: "flex", alignItems: "baseline", justifyContent: "space-between",
      }}
    >
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div style={{
            fontFamily: "'Barlow Condensed', sans-serif", fontSize: "1.4rem",
            fontWeight: 700, letterSpacing: "0.06em", color: "#FFFFFF",
          }}>
            Løp {race.race_number}
          </div>
          <StartMethodBadge method={race.start_method} />
        </div>
        <div style={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.5)", marginTop: "2px" }}>
          {race.distance ? `${race.distance} m` : ""}
          {race.horses?.length ? ` · ${race.horses.length} hester` : ""}
        </div>
      </div>
      {startStr && (
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.12em", color: "rgba(255,255,255,0.4)" }}>
            Start
          </div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "1.2rem", fontWeight: 700, color: "#FFD700" }}>
            {startStr}
          </div>
        </div>
      )}
    </div>
  );
}

function HorseList({ horses, raceKey, raceNumber, winners = [], silksByName = {} }) {
  if (!horses?.length) {
    return (
      <div style={{
        padding: "24px 0", textAlign: "center", color: "rgba(255,255,255,0.4)",
        fontSize: "0.9rem",
      }}>
        Ingen hester tilgjengelig.
      </div>
    );
  }
  const [bg, fg] = blanketFor(raceNumber);
  // White blankets need a subtle border to stand out on the dark page
  const badgeBorder = bg.toUpperCase() === "#FFFFFF" ? "2px solid rgba(0,0,0,0.15)" : "none";
  return horses.map((horse, i) => {
    const isWinner = winners.includes(horse.number);
    return (
    <div
      key={`${raceKey}-${horse.number}`}
      className="rank-row"
      data-testid={`horse-row-${raceKey}-${horse.number}`}
      style={{
        display: "flex", alignItems: "center", gap: "8px",
        padding: "16px 10px",
        backgroundColor: isWinner ? "rgba(255,215,0,0.08)" : i % 2 === 0 ? "rgba(255,255,255,0.03)" : "transparent",
        border: isWinner ? "1px solid rgba(255,215,0,0.45)" : "1px solid transparent",
        borderRadius: "14px", marginBottom: "10px",
        animationDelay: `${Math.min(i * 40, 200)}ms`,
      }}
    >
      <div style={{
        width: "22px", flexShrink: 0, textAlign: "center",
        fontFamily: "'JetBrains Mono', monospace", fontSize: "1.2rem",
        fontWeight: 700, color: "rgba(255,255,255,0.45)",
      }}>
        {i + 1}
      </div>
      <div
        data-testid={`horse-badge-${horse.number}`}
        style={{
          width: "64px", height: "64px", flexShrink: 0,
          backgroundColor: bg, color: fg,
          border: badgeBorder, boxSizing: "border-box",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontFamily: "'JetBrains Mono', monospace", fontWeight: 700,
          fontSize: "2rem", borderRadius: "10px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.35)",
        }}
      >
        {horse.number}
      </div>
      {(() => {
        const silk = silksByName[(horse.driver || "").trim().toLowerCase()];
        if (!silk) return null;
        return (
          <div
            data-testid={`silk-icon-${raceKey}-${horse.number}`}
            style={{
              width: "64px", height: "64px", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <SilkIcon
              body={silk.body_color}
              sleeve={silk.sleeve_color}
              helmet={silk.helmet_color}
              pattern={silk.pattern}
              patternColor={silk.pattern_color}
              size={50}
            />
          </div>
        );
      })()}
      <div style={{ flex: 1, overflow: "hidden" }}>
        <div style={{
          fontSize: "1.35rem", fontWeight: 700, color: "#FFFFFF",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          lineHeight: 1.15,
          display: "flex", alignItems: "center", gap: "8px",
        }}>
          {isWinner && (
            <Star
              data-testid={`winner-star-${raceKey}-${horse.number}`}
              size={20}
              fill="#FFD700"
              color="#FFD700"
              style={{ flexShrink: 0 }}
            />
          )}
          <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{horse.name}</span>
        </div>
        {horse.driver && (
          <div style={{
            fontSize: "0.9rem", color: "rgba(255,255,255,0.65)", marginTop: "3px",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {horse.driver}
          </div>
        )}
        {horse.distribution > 0 && (
          <div style={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.4)", marginTop: "3px" }}>
            {horse.distribution.toFixed(1)}%
          </div>
        )}
      </div>
      <div style={{ textAlign: "right", flexShrink: 0, minWidth: "72px" }}>
        <div style={{
          fontFamily: "'JetBrains Mono', monospace", fontSize: "1.75rem",
          fontWeight: 700, color: "#FFD700", lineHeight: 1,
        }}>
          {horse.odds ? horse.odds.toFixed(2) : "—"}
        </div>
        <div style={{
          fontSize: "0.7rem", textTransform: "uppercase",
          letterSpacing: "0.14em", color: "rgba(255,255,255,0.4)", marginTop: "5px",
        }}>
          odds
        </div>
      </div>
    </div>
    );
  });
}
